import React, { useState, useRef, useEffect, useReducer } from 'react';
import { Ship, ChevronDown, Plus, Building2, Check } from 'lucide-react';
import { useVessel } from '../../contexts/VesselContext';
import { usePermissions } from '../../hooks/usePermissions';

const VesselSelector = ({ onAddVessel, onFleetOverview }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { vessels, enrichedVessels, currentVesselId, setCurrentVessel, getCurrentVessel } = useVessel();
  const { can, currentRole } = usePermissions();
  // Get current vessel reactively - will update when vessels or currentVesselId changes
  const currentVessel = React.useMemo(() => {
    return getCurrentVessel();
  }, [getCurrentVessel, vessels, currentVesselId]);
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  
  // Listen for vessel updates to refresh the display
  React.useEffect(() => {
    const handleVesselUpdate = () => {
      forceUpdate();
    };
    
    window.addEventListener('vesselUpdated', handleVesselUpdate);
    window.addEventListener('vesselChanged', handleVesselUpdate);
    
    return () => {
      window.removeEventListener('vesselUpdated', handleVesselUpdate);
      window.removeEventListener('vesselChanged', handleVesselUpdate);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleVesselSelect = (vesselId) => {
    setCurrentVessel(vesselId);
    setIsOpen(false);
  };

  const handleFleetOverview = () => {
    setIsOpen(false);
    if (onFleetOverview) {
      onFleetOverview();
    }
  };

  const handleAddVessel = () => {
    setIsOpen(false);
    if (onAddVessel) {
      onAddVessel();
    }
  };

  // Get vessel type display name
  const getVesselTypeDisplay = (type) => {
    const types = {
      rib: 'RIB',
      tug: 'Tug',
      pilot: 'Pilot Boat',
      pax: 'Passenger',
      ctv: 'CTV',
      survey: 'Survey',
      support: 'Support',
      patrol: 'Patrol'
    };
    return types[type] || type;
  };

  // Get WBC category display — returns null if no valid category
  const getCategoryDisplay = (category) => {
    if (!category) return null;
    if (typeof category === 'string' && category.startsWith('cat')) {
      return category.replace('cat', 'Category ');
    }
    return `Category ${category}`;
  };

  // Build subtitle — FT-231: use filter-join to handle empty type or category gracefully
  const getVesselSubtitle = (vessel) => {
    const type = getVesselTypeDisplay(vessel.type);
    const cat = getCategoryDisplay(vessel.category);
    return [type, cat].filter(Boolean).join(' • ');
  };

  return (
    <div className="relative max-w-xs" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 sm:gap-2 p-2 sm:px-3 sm:py-2 text-sm font-medium text-white lg:text-gray-700 bg-transparent lg:bg-white border border-white/20 lg:border-gray-300 rounded-lg hover:bg-white/10 lg:hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 min-w-fit"
      >
        <Ship className="h-4 w-4 text-white/80 lg:text-gray-500 flex-shrink-0" />

        {/* FT-188: Mobile = icon only, Tablet/Desktop = show vessel name */}
        {/* Mobile users see vessel name in dashboard subtitle instead */}
        <div className="text-left flex-1 min-w-0 hidden sm:block">
          <div className="font-semibold truncate">
            {/* Tablet (sm to lg): Vessel name */}
            <span className="lg:hidden">{currentVessel?.name || 'Select'}</span>
            {/* Desktop (lg+): Full vessel name */}
            <span className="hidden lg:inline">{currentVessel?.name || 'Select Vessel'}</span>
          </div>
          {currentVessel && (
            <div className="text-xs text-white/60 lg:text-gray-500 hidden lg:block">
              {getVesselSubtitle(currentVessel)}
            </div>
          )}
        </div>
        <ChevronDown className={`h-3 w-3 sm:h-4 sm:w-4 text-white/60 lg:text-gray-500 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''} hidden sm:block`} />
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 origin-top-left rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5" style={{ zIndex: 9999 }}>
          <div className="py-1">
            {/* Vessel list - FT-231: Use enrichedVessels for type/category display */}
            {(enrichedVessels || []).map((vessel) => (
              <button
                key={vessel.id}
                onClick={() => handleVesselSelect(vessel.id)}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                <div className="flex-1 text-left">
                  <div className="font-medium flex items-center gap-2">
                    {vessel.name}
                    {vessel.id === currentVesselId && (
                      <Check className="h-4 w-4 text-primary-600" />
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {getVesselSubtitle(vessel)}
                    {vessel.port && ` • ${vessel.port}`}
                  </div>
                </div>
              </button>
            ))}

            {/* Divider */}
            <div className="border-t border-gray-200 my-1" />

            {/* Fleet Overview */}
            <button
              onClick={handleFleetOverview}
              className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
            >
              <Building2 className="h-4 w-4 mr-2 text-gray-500" />
              Fleet Overview
            </button>

            {/* Add Vessel - Fleet role only */}
            {can('create', 'vessel') && (
              <button
                onClick={handleAddVessel}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              >
                <Plus className="h-4 w-4 mr-2 text-gray-500" />
                Add Vessel
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VesselSelector;